import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { TEACHER_ASSIGNMENT_V2_INSIGHTS_URL } from 'config/urls';
import { first, get, isEmpty } from 'lodash';
import { noop } from 'utils';

import { GRADE_TAB } from 'pages/Teachers/Insights/constants';
import { withNavbar } from 'pages/Teachers/shared';
import { useMixpanelPageOpenTrack } from 'utils/integrations/mixpanel';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { parseParams, reverse, stringifyParams } from 'utils/urls';

import UserContext from 'components/UserContext';

import {
  AssignmentSkeleton,
  CelebratoryDialog,
  EmptyAssignment,
  FeedbackTool
} from './components';
import { fetchStudentAssignmentDetail } from './sdk';
import styles from './styles.module.scss';
import {
  changePageParams,
  getObjectFromListOrFirst,
  useCelebratoryDialogActions,
  useEnsurePageParams,
  useNavigation,
  usePreviousAndNextTasks,
  useSections
} from './utils';

const Feedback = ({ match, history }) => {
  useMixpanelPageOpenTrack('Teacher opened Feedback page');
  const user = useContext(UserContext);

  const urlParams = parseParams(history.location.search);
  const trackerId = get(match, 'params.trackerId');

  const [loadingEnded, setLoadingEnded] = useState(false);
  const [sections, fetchSections] = useSections();
  const [selectedSection, setSelectedSection] = useState(null);

  const students = useMemo(
    () => get(selectedSection, 'students', []),
    [selectedSection]
  );
  const [selectedStudent, setSelectedStudent] = useState();
  const [studentAssignment, setStudentAssignment] = useState();

  const fetchAssignment = ({ studentId, sectionId }) =>
    fetchStudentAssignmentDetail({
      assignmentId: sectionId,
      studentId
    }).then((assignment) => {
      setStudentAssignment(assignment);
      return assignment;
    });

  const refetchAssignment = () => {
    return fetchStudentAssignmentDetail({
      assignmentId: studentAssignment.id,
      studentId: selectedStudent.id
    }).then((assignment) => {
      setStudentAssignment(assignment);
      return assignment;
    });
  };

  useEffect(() => {
    if (selectedStudent && selectedSection && !studentAssignment) {
      fetchAssignment({
        studentId: selectedStudent.id,
        sectionId: selectedSection.id
      });
    }
  }, [selectedStudent, selectedSection, studentAssignment]);

  const tasks = get(studentAssignment, 'tasks', []);
  const [activeTask, setActiveTask] = useState();
  const [activeWork, setActiveWork] = useState();

  useEffect(() => {
    // Reset the whole page state when the whole assignment is changed
    setSelectedSection(null);
    setSelectedStudent(null);
    setStudentAssignment(null);
    setActiveTask(null);
    setActiveWork(null);
  }, [trackerId]);

  const [
    isCelebratoryDialogVisible,
    setIsCelebratoryDialogVisible,
    closestAssignment,
    setClosestAssignment,
    goToAssignmentResults,
    goToNextAssignment
  ] = useCelebratoryDialogActions({
    trackerId,
    sectionId: selectedSection?.id
  });

  useEnsurePageParams({
    urlParams,
    selectedSectionId: get(selectedSection, 'id'),
    selectedStudentId: get(selectedStudent, 'id'),
    activeTaskId: get(activeTask, 'id'),
    workItemId: get(activeWork, 'id')
  });

  useEffect(() => {
    if (!selectedSection) {
      fetchSections(trackerId).then((sections) => {
        setSelectedSection(
          getObjectFromListOrFirst(sections, urlParams.section)
        );
      });
    }
  }, [urlParams.section, selectedSection, fetchSections, trackerId]);

  useEffect(() => {
    if (!selectedStudent) {
      setSelectedStudent(getObjectFromListOrFirst(students, urlParams.student));
    }
  }, [selectedStudent, students, urlParams.student, setSelectedStudent]);

  useEffect(() => {
    if (studentAssignment && !activeTask) {
      setActiveTask(getObjectFromListOrFirst(tasks, urlParams.task));
    }
  }, [studentAssignment, activeTask, tasks, urlParams.task]);

  useEffect(() => {
    setActiveWork(
      getObjectFromListOrFirst(get(activeTask, 'work', []), urlParams.work)
    );
  }, [activeTask, urlParams.work]);

  const [previousTask, nextTask] = usePreviousAndNextTasks({
    tasks,
    task: activeTask
  });

  useEffect(() => {
    if (selectedSection && isEmpty(students)) {
      setLoadingEnded(true);
    }
  }, [selectedSection, students]);

  const onCanvasLoaded = () => {
    setLoadingEnded(true);
  };

  const changeSelectedSection = useCallback((section) => {
    const firstStudent = first(section.students);

    setSelectedSection(section);
    setSelectedStudent(firstStudent);
  }, []);

  const switchAssignment = useCallback(
    ({ assignment, task, studentId, sectionId }) => {
      // If task is set - then there is a next assignment to show
      if (task) {
        const section = getObjectFromListOrFirst(sections, sectionId);
        const student = getObjectFromListOrFirst(section.students, studentId);
        const nextTask = { ...task, alreadyRendered: true };
        const workItem = first(get(task, 'work', []));

        setSelectedSection(section);
        setSelectedStudent(student);
        setStudentAssignment(assignment);
        setActiveTask(nextTask);
        setActiveWork(workItem);
      }
    },
    [sections]
  );

  const switchAssignmentFromNewTracker = useCallback(() => {
    goToNextAssignment()
      .then(({ sectionId, studentId }) => {
        changePageParams({
          selectedStudentId: studentId,
          selectedSectionId: sectionId,
          activeTaskId: null,
          workItemId: null
        });

        setSelectedSection(null);
        setSelectedStudent(null);
        setStudentAssignment(null);
        setActiveTask(null);
        setActiveWork(null);
      })
      .catch(noop);
  }, [goToNextAssignment]);

  const onSectionSelect = useCallback(
    (newSection) => {
      setLoadingEnded(false);

      const firstStudent = first(newSection.students);
      changeSelectedSection(newSection);

      if (firstStudent) {
        fetchAssignment({
          studentId: firstStudent.id,
          sectionId: newSection.id
        }).then((assignment) => {
          setStudentAssignment(assignment);
          const task = first(assignment.tasks);
          setActiveTask(task);
          setActiveWork(first(get(task, 'work', [])));
        });
      }
    },
    [changeSelectedSection]
  );

  const onStudentSelect = useCallback(
    (newStudent) => {
      trackMixpanelEvent(
        user.id,
        `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
        {
          feature: 'assignments',
          view: 'feedback',
          button: 'student_toggle'
        }
      );
      setLoadingEnded(false);

      setSelectedStudent(newStudent);

      if (selectedSection) {
        fetchAssignment({
          studentId: newStudent.id,
          sectionId: selectedSection.id
        }).then((assignment) => {
          setStudentAssignment(assignment);
          const task = first(assignment.tasks);
          setActiveTask(task);
          setActiveWork(first(get(task, 'work', [])));
        });
      }
    },
    [selectedSection, user.id]
  );

  const onTitleAndBackClick = useCallback(() => {
    return history.push({
      pathname: reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
        trackerInstanceSectionId: selectedSection.id
      }),
      search: stringifyParams({
        activeTab: GRADE_TAB
      })
    });
  }, [selectedSection, history]);
  const assignmentName = useMemo(
    () => get(studentAssignment, 'name'),
    [studentAssignment]
  );

  useNavigation({
    assignmentName,
    onTitleClick: onTitleAndBackClick,
    sections,
    selectedSection,
    onSectionSelect,
    students,
    selectedStudent,
    onStudentSelect,
    onBackButtonClick: onTitleAndBackClick
  });

  const noActiveStudentWork =
    loadingEnded && (!activeWork || isEmpty(students));

  return (
    <div className={styles.container}>
      {noActiveStudentWork && <EmptyAssignment />}
      {!loadingEnded && !activeWork && <AssignmentSkeleton />}
      {!noActiveStudentWork && activeWork && activeTask && (
        <FeedbackTool
          activeTask={activeTask}
          activeWork={activeWork}
          setActiveTask={setActiveTask}
          setActiveWork={setActiveWork}
          onCanvasLoaded={onCanvasLoaded}
          previousTask={previousTask}
          nextTask={nextTask}
          refetchAssignment={refetchAssignment}
          switchAssignment={switchAssignment}
          fetchAssignment={fetchAssignment}
          setStudentAssignment={setStudentAssignment}
          setShowCelebratoryDialog={setIsCelebratoryDialogVisible}
          setClosestAssignment={setClosestAssignment}
        />
      )}
      <CelebratoryDialog
        open={isCelebratoryDialogVisible}
        assignmentName={assignmentName}
        onClose={() => setIsCelebratoryDialogVisible(false)}
        nextAssignment={closestAssignment}
        goToNextAssignment={switchAssignmentFromNewTracker}
        goToAssignmentResults={goToAssignmentResults}
      />
    </div>
  );
};

export default withNavbar(Feedback);
